import { useState, useCallback, useMemo, useEffect } from "react";
import {
  Question,
  QuestionTreeResponse,
  AnswersMap,
  Answer,
} from "../types/questionTree.types";
import { useVisibilityEngine } from "./useVisibilityEngine";

interface UseQuestionTreeProps {
  treeData: QuestionTreeResponse | null;
  onSaveAnswer?: (questionId: number, questionName: string, value: string) => Promise<void>;
  onQuestionsCompleted?: () => Promise<void>;
}

interface UseQuestionTreeReturn {
  // Estado actual
  currentQuestion: Question | null;
  currentIndex: number;
  answers: AnswersMap;
  questions: Question[];
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  isCompleted: boolean;

  // Progreso
  progress: number;
  currentStep: number;
  totalSteps: number;

  // Acciones
  setAnswer: (value: string) => void;
  goToNext: () => Promise<void>;
  goToPrevious: () => void;
  goToQuestion: (questionId: number) => void;
  setIsCompleted: (value: boolean) => void;

  // Utilidades
  getCurrentAnswer: () => string | undefined;
  isCurrentQuestionAnswered: () => boolean;
  getVisibleQuestions: () => Question[];
}

/**
 * Hook principal para manejar el árbol de preguntas
 * Gestiona el estado, navegación y persistencia de respuestas
 */
/**
 * Construye el mapa de respuestas inicial a partir de los datos del árbol
 */
const buildInitialAnswers = (
  treeData: QuestionTreeResponse | null
): AnswersMap => {
  if (!treeData?.answers || !treeData?.questions) {
    return {};
  }

  const initialAnswers: AnswersMap = {};

  treeData.answers.forEach((answer: Answer) => {
    // Buscar la pregunta para obtener el nombre del campo
    const question = treeData.questions.find((q) => q.id === answer.question_id);
    if (question) {
      // Extraer el nombre del campo (ej: "LeadCustom::haveInsurance" -> "haveInsurance")
      const fieldName = question.field.split("::").pop() || question.name;
      initialAnswers[fieldName] = answer.value;
      // También guardar con el name de la pregunta para las reglas de visibilidad
      if (question.name && question.name !== fieldName) {
        initialAnswers[question.name] = answer.value;
      }
    }
  });

  return initialAnswers;
};

export const useQuestionTree = ({
  treeData,
  onSaveAnswer,
  onQuestionsCompleted,
}: UseQuestionTreeProps): UseQuestionTreeReturn => {
  // Pasar datos del lead al engine de visibilidad para evaluar reglas con source: "lead"
  const {
    isQuestionVisible,
    getNextVisibleQuestionIndex,
    getPreviousVisibleQuestionIndex,
    getVisibleQuestions: getVisibleQuestionsFromEngine,
    countVisibleQuestions,
  } = useVisibilityEngine(treeData?.lead);

  const questions = treeData?.questions ?? [];

  // Calcular respuestas iniciales síncronamente para que estén disponibles en el primer render
  const computedAnswers = useMemo(() => buildInitialAnswers(treeData), [treeData]);

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [localAnswers, setLocalAnswers] = useState<AnswersMap>({});
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  // Combinar las respuestas computadas con las locales (las locales tienen prioridad)
  // Esto asegura que las respuestas del servidor estén disponibles inmediatamente
  const answers = useMemo(
    () => ({ ...computedAnswers, ...localAnswers }),
    [computedAnswers, localAnswers]
  );

  // Cuando cambia treeData, posicionar en la primera pregunta sin responder y resetear estado local
  useEffect(() => {
    if (treeData?.answers) {
      // Resetear respuestas locales cuando cambia el treeData
      setLocalAnswers({});

      // Usar computedAnswers directamente ya que se recalculó con el nuevo treeData
      const answersToUse = buildInitialAnswers(treeData);

      // Posicionar en la primera pregunta sin responder
      const firstUnansweredIndex = findFirstUnansweredQuestion(
        questions,
        answersToUse
      );

      if (firstUnansweredIndex === -1) {
        // Todas las preguntas están respondidas, mostrar resumen
        setIsCompleted(true);
      } else {
        // Hay preguntas sin responder, posicionar en la primera
        setIsCompleted(false);
        setCurrentIndex(firstUnansweredIndex);
      }
    }
  }, [treeData]);

  /**
   * Encuentra la primera pregunta visible sin responder
   * Retorna -1 si todas las preguntas visibles están respondidas (cuestionario completo)
   */
  const findFirstUnansweredQuestion = (
    questions: Question[],
    answers: AnswersMap
  ): number => {
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      if (isQuestionVisible(question, answers)) {
        const fieldName = question.field.split("::").pop() || question.name;
        // Verificar si el campo existe en answers (permite string vacío como respuesta válida)
        if (!(fieldName in answers)) {
          return i;
        }
      }
    }
    // Todas las preguntas visibles están respondidas
    return -1;
  };

  // Pregunta actual
  const currentQuestion = useMemo(() => {
    if (questions.length === 0 || currentIndex < 0) return null;
    return questions[currentIndex] || null;
  }, [questions, currentIndex]);

  // Obtener las preguntas visibles actuales
  const getVisibleQuestions = useCallback(() => {
    return getVisibleQuestionsFromEngine(questions, answers);
  }, [questions, answers, getVisibleQuestionsFromEngine]);

  // Calcular progreso
  const visibleQuestions = getVisibleQuestions();
  const totalSteps = visibleQuestions.length;
  const currentStep = useMemo(() => {
    if (!currentQuestion) return 0;
    const idx = visibleQuestions.findIndex((q) => q.id === currentQuestion.id);
    return idx + 1;
  }, [visibleQuestions, currentQuestion]);

  const progress = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0;

  // Verificar si es primera o última pregunta visible
  const isFirstQuestion = useMemo(() => {
    return getPreviousVisibleQuestionIndex(questions, answers, currentIndex) === null;
  }, [questions, answers, currentIndex, getPreviousVisibleQuestionIndex]);

  const isLastQuestion = useMemo(() => {
    return getNextVisibleQuestionIndex(questions, answers, currentIndex) === null;
  }, [questions, answers, currentIndex, getNextVisibleQuestionIndex]);

  // Obtener respuesta actual
  const getCurrentAnswer = useCallback((): string | undefined => {
    if (!currentQuestion) return undefined;
    const fieldName =
      currentQuestion.field.split("::").pop() || currentQuestion.name;
    return answers[fieldName];
  }, [currentQuestion, answers]);

  // Verificar si la pregunta actual está respondida
  // Permite string vacío como respuesta válida (ej: spain_nationality_question donde "" = "No")
  const isCurrentQuestionAnswered = useCallback((): boolean => {
    if (!currentQuestion) return false;
    const fieldName =
      currentQuestion.field.split("::").pop() || currentQuestion.name;
    return fieldName in answers;
  }, [currentQuestion, answers]);

  // Establecer respuesta
  const setAnswer = useCallback(
    (value: string) => {
      if (!currentQuestion) return;

      const fieldName =
        currentQuestion.field.split("::").pop() || currentQuestion.name;

      setLocalAnswers((prev) => {
        const newAnswers = {
          ...prev,
          [fieldName]: value,
        };
        // También guardar con el name de la pregunta para las reglas de visibilidad
        if (currentQuestion.name && currentQuestion.name !== fieldName) {
          newAnswers[currentQuestion.name] = value;
        }
        return newAnswers;
      });
    },
    [currentQuestion]
  );

  // Ir a la siguiente pregunta (espera al guardado durante la transición visual)
  const goToNext = useCallback(async () => {
    if (!currentQuestion) return;

    const fieldName =
      currentQuestion.field.split("::").pop() || currentQuestion.name;
    let currentAnswer = answers[fieldName];

    // Si es checkbox_optional sin respuesta, establecer "none"
    if (currentQuestion.type === "checkbox_optional" && !currentAnswer) {
      currentAnswer = "none";
      setLocalAnswers((prev) => {
        const newAnswers = { ...prev, [fieldName]: "none" };
        if (currentQuestion.name && currentQuestion.name !== fieldName) {
          newAnswers[currentQuestion.name] = "none";
        }
        return newAnswers;
      });
    }

    // Crear mapa de respuestas actualizado para calcular visibilidad correctamente
    const updatedAnswers: AnswersMap = { ...answers, [fieldName]: currentAnswer };
    if (currentQuestion.name && currentQuestion.name !== fieldName) {
      updatedAnswers[currentQuestion.name] = currentAnswer;
    }

    // Calcular siguiente pregunta ANTES de cualquier operación async
    const nextUnansweredIndex = findNextUnansweredQuestion(
      questions,
      updatedAnswers,
      currentIndex
    );

    const isLastQuestion = nextUnansweredIndex === -1;

    // Guardar respuesta en el servidor (la transición visual cubre este tiempo)
    if (onSaveAnswer && currentAnswer) {
      try {
        await onSaveAnswer(currentQuestion.id, currentQuestion.name, currentAnswer);
      } catch (error) {
        console.error("Error saving answer:", error);
      }
    }

    // Navegar a la siguiente pregunta o mostrar resumen
    if (!isLastQuestion) {
      setCurrentIndex(nextUnansweredIndex);
    } else {
      // Recargar datos del backend antes de mostrar resumen
      // El useEffect de treeData verificará si hay nuevas preguntas sin responder
      // y decidirá si mostrar el resumen o posicionar en la nueva pregunta
      if (onQuestionsCompleted) {
        try {
          await onQuestionsCompleted();
          // No hacemos setIsCompleted(true) aquí porque el useEffect lo manejará
          // al recibir el nuevo treeData y verificar si hay preguntas pendientes
        } catch (error) {
          console.error("Error refreshing data:", error);
          // Si falla la recarga, mostramos el resumen con los datos locales
          setIsCompleted(true);
        }
      } else {
        setIsCompleted(true);
      }
    }
  }, [
    currentQuestion,
    answers,
    currentIndex,
    questions,
    onSaveAnswer,
    onQuestionsCompleted,
    isQuestionVisible,
  ]);

  /**
   * Encuentra la siguiente pregunta visible sin responder después del índice actual
   */
  const findNextUnansweredQuestion = (
    questions: Question[],
    answers: AnswersMap,
    fromIndex: number
  ): number => {
    // Primero buscar después del índice actual
    for (let i = fromIndex + 1; i < questions.length; i++) {
      const question = questions[i];
      if (isQuestionVisible(question, answers)) {
        const fieldName = question.field.split("::").pop() || question.name;
        // Verificar si el campo existe en answers (permite string vacío como respuesta válida)
        if (!(fieldName in answers)) {
          return i;
        }
      }
    }
    // Si no hay ninguna después, buscar desde el principio (por si cambió la visibilidad)
    for (let i = 0; i <= fromIndex; i++) {
      const question = questions[i];
      if (isQuestionVisible(question, answers)) {
        const fieldName = question.field.split("::").pop() || question.name;
        // Verificar si el campo existe en answers (permite string vacío como respuesta válida)
        if (!(fieldName in answers)) {
          return i;
        }
      }
    }
    // Todas las preguntas visibles están respondidas
    return -1;
  };

  // Ir a la pregunta anterior
  const goToPrevious = useCallback(() => {
    const prevIndex = getPreviousVisibleQuestionIndex(
      questions,
      answers,
      currentIndex
    );

    if (prevIndex !== null) {
      setCurrentIndex(prevIndex);
    }
  }, [questions, answers, currentIndex, getPreviousVisibleQuestionIndex]);

  // Ir a una pregunta específica por ID
  const goToQuestion = useCallback(
    (questionId: number) => {
      const index = questions.findIndex((q) => q.id === questionId);
      if (index !== -1 && isQuestionVisible(questions[index], answers)) {
        setCurrentIndex(index);
      }
    },
    [questions, answers, isQuestionVisible]
  );

  return {
    currentQuestion,
    currentIndex,
    answers,
    questions,
    isFirstQuestion,
    isLastQuestion,
    isCompleted,
    progress,
    currentStep,
    totalSteps,
    setAnswer,
    goToNext,
    goToPrevious,
    goToQuestion,
    setIsCompleted,
    getCurrentAnswer,
    isCurrentQuestionAnswered,
    getVisibleQuestions,
  };
};
